import { type ReactNode } from 'react';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';

import { Page } from '@/components/layout/Page';
import { Section } from '@/components/layout/Section';
import { PageHeader } from '@/components/composed/PageHeader';
import { RouteError } from '@/components/composed/RouteError';
import { NotFound } from '@/components/composed/NotFound';
import { EntitySectionPanel } from '@/components/composed/EntitySectionPanel';
import {
  EntityRelationshipsView,
  entityDetailQuery,
} from '@/features/entities';
import { renderBodyForSection } from './-section-content';
import { entityId } from '@/lib/api/ids';
import { isValidSection } from '@/lib/entity-sections';
import { sectionsForKind } from '@/lib/entity-composition';
import { gateSectionsForSession } from '@/lib/entity-section-access';
import { useAuthStore } from '@/lib/session/auth-store';
import { usePageMeta } from '@/app/shell/use-page-meta';
import { useRailSubNav } from '@/app/shell/use-rail-sub-nav';

/*
 * The organization shell and its kind-scoped section navigation (E02.F09).
 *
 * One route renders every kind of organization, reading the ONE composition
 * statement (lib/entity-composition) for the kind the BFF returns — so no route
 * carries its own section list and two routes can never disagree about a kind's
 * sections (R117, feature AC-7583b31d). The section param addresses a specific
 * section (R120 AC-ab628dc1 / AC-ce65ad81); opening the organization lands on
 * the profile (R119, via the index redirect).
 *
 * Unknown-section → profile fallback (R121). `beforeLoad` resolves the entity's
 * kind and, if the section is not one that kind offers (or is one the person
 * may not use), redirects to the profile BEFORE anything renders. Because
 * `beforeLoad` re-runs on every navigation — first open AND back/forward
 * history (AC-aa167d2e) — the fallback holds through browser history, which is
 * where a mount-only guard would break.
 *
 * The relationships view (F04's own record of every relationship the org is
 * party to) renders in the shell above the section nav, so the organization's
 * record stays reachable while a person moves between its sections — and the
 * top-level navigation and System section stay reachable throughout (R115
 * AC-69e7fadd; the shell keeps the primary Sidebar mounted).
 */
export const Route = createFileRoute('/_auth/entities/$entityId/$section')({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      entityDetailQuery(entityId(params.entityId)),
    ),
  beforeLoad: async ({ context, params }) => {
    // Resolve the kind to know which sections this organization offers. The
    // detail read is scope-checked by the server; a request for an organization
    // the caller is not entitled to is answered generically at the 401/403
    // boundary (R125 AC-2c40408a) — not disclosed here.
    const entity = await context.queryClient.ensureQueryData(
      entityDetailQuery(entityId(params.entityId)),
    );
    // The sections this kind offers, narrowed to those the person may use
    // (courtesy gate, R116). The fallback target is always the profile — the
    // safest resolution for an unknown or unavailable section (AC-621c4b05).
    const session = useAuthStore.getState().session;
    const allowed = gateSectionsForSession(
      sectionsForKind(entity.kind),
      session,
    );
    if (!isValidSection(params.section, allowed)) {
      throw redirect({
        to: '/entities/$entityId/$section',
        params: { entityId: params.entityId, section: 'profile' },
        replace: true,
      });
    }
  },
  component: EntitySectionPage,
  errorComponent: RouteError,
  notFoundComponent: NotFound,
});

/*
 * Injects a typed router <Link> into the domain-free RailSubNav. The rail
 * builds the full `/entities/{id}/{section}` href; we pass it straight through
 * so every param survives — client navigation and deep-linking both work, and
 * the links are real anchors a keyboard reaches (R122 AC-18848884).
 */
function renderLink(props: {
  href: string;
  children: ReactNode;
  className?: string;
  'aria-current'?: 'page';
  'data-section'?: string;
}) {
  const { href, children, ...rest } = props;
  return (
    <Link to={href} {...rest}>
      {children}
    </Link>
  );
}

function EntitySectionPage() {
  const params = Route.useParams();
  const id = entityId(params.entityId);
  const { data: entity } = useSuspenseQuery(entityDetailQuery(id));

  // The sections this kind offers, gated to what the person may use. The
  // beforeLoad guard has already redirected an unknown/unavailable section to
  // the profile, so `params.section` is always a member of `allowed` here.
  const session = useAuthStore((s) => s.session);
  const allowed = gateSectionsForSession(sectionsForKind(entity.kind), session);
  const activeSectionId = params.section;
  const activeSection =
    allowed.find((s) => s.id === activeSectionId) ?? allowed[0]!;

  usePageMeta({ title: entity.legalName });
  // Publish the kind-scoped section sub-nav into the shell rail. Domain data
  // (sections, active id, name) originates here and flows to domain-free chrome;
  // the primary nav and System section stay mounted alongside it (R115).
  useRailSubNav({
    sections: allowed,
    activeSectionId: activeSection.id,
    basePath: `/entities/${id}`,
    entityName: entity.legalName,
    backHref: '/entities',
    backLabel: 'All organizations',
    navLabel: `${entity.kind} sections`,
    renderLink,
  });

  return (
    <Page>
      <PageHeader
        title={entity.legalName}
        description={`${entity.kind} · ${entity.publicId}`}
      />
      {/*
       * The organization's own record (F04): every relationship it is party to
       * in either direction, created/corrected/ended here. Kept in the shell so
       * the record stays reachable as the person moves between sections.
       */}
      <Section>
        <EntityRelationshipsView entity={entity} />
      </Section>
      {/*
       * The active section's body, in a focusable, self-announcing panel: focus
       * moves here when a section opens (R122 AC-e7c78da1) and a screen reader
       * is told which section is now open, un-announcing the previous (R123).
       */}
      <Section>
        <EntitySectionPanel
          sectionId={activeSection.id}
          sectionLabel={activeSection.label}
        >
          {renderBodyForSection(entity.kind, activeSection.id, id)}
        </EntitySectionPanel>
      </Section>
    </Page>
  );
}
